/**
 * 统一图标注册表
 *
 * 所有图标名称与渲染策略的集中定义。
 * 新增图标只需在此添加一条记录，无需修改组件源码。
 *
 * 渲染策略（source）：
 * - 'material-symbols'  : Google Material Symbols 字体图标
 * - 'local-svg'         : src/assets/svgs/ 下的本地 SVG 文件
 * - 'iconify'           : Iconify 图标集（Element Plus / Ant Design / TDesign）
 */

export type IconSource = 'material-symbols' | 'local-svg' | 'iconify'

export interface IconDefinition {
  /** 对外统一名称（camelCase） */
  name: string
  /** 渲染策略 */
  source: IconSource
  /** 底层实际渲染名 */
  rawName: string
  /** 搜索关键词（供 IconPicker 使用） */
  keywords?: string[]
}

export const ICON_REGISTRY: Record<string, IconDefinition> = {
  // ============================================================
  // Material Symbols（Google Fonts）
  // 命名风格：snake_case（与 Material Symbols 官方名称一致）
  // ============================================================

  // 菜单导航图标
  customer: {
    name: 'customer',
    source: 'material-symbols',
    rawName: 'group',
    keywords: ['客户', '用户', 'people']
  },
  operations: {
    name: 'operations',
    source: 'material-symbols',
    rawName: 'admin_panel_settings',
    keywords: ['运维', '管理', 'admin']
  },
  agent: {
    name: 'agent',
    source: 'material-symbols',
    rawName: 'smart_toy',
    keywords: ['智能体', 'AI', '机器人']
  },
  systemSettings: {
    name: 'systemSettings',
    source: 'material-symbols',
    rawName: 'settings',
    keywords: ['设置', '系统', '配置']
  },
  template: {
    name: 'template',
    source: 'material-symbols',
    rawName: 'article',
    keywords: ['模板', '文档', '文章']
  },
  menuManagement: {
    name: 'menuManagement',
    source: 'material-symbols',
    rawName: 'menu',
    keywords: ['菜单', '导航', '目录']
  },
  roleManagement: {
    name: 'roleManagement',
    source: 'material-symbols',
    rawName: 'person',
    keywords: ['角色', '权限', '用户']
  },

  // 页面装饰图标
  collapse: {
    name: 'collapse',
    source: 'material-symbols',
    rawName: 'keyboard_double_arrow_left',
    keywords: ['折叠', '展开', '侧边栏']
  },
  psychology: {
    name: 'psychology',
    source: 'material-symbols',
    rawName: 'psychology',
    keywords: ['心理', '思维', '创意']
  },
  libraryBooks: {
    name: 'libraryBooks',
    source: 'material-symbols',
    rawName: 'library_books',
    keywords: ['文献', '书籍', '图书馆']
  },
  lightbulb: {
    name: 'lightbulb',
    source: 'material-symbols',
    rawName: 'lightbulb',
    keywords: ['灵感', '灯泡', '选题']
  },
  accountTree: {
    name: 'accountTree',
    source: 'material-symbols',
    rawName: 'account_tree',
    keywords: ['框架', '大纲', '树']
  },
  rateReview: {
    name: 'rateReview',
    source: 'material-symbols',
    rawName: 'rate_review',
    keywords: ['评审', '评估', '审阅']
  },
  chevronLeft: {
    name: 'chevronLeft',
    source: 'material-symbols',
    rawName: 'chevron_left',
    keywords: ['左箭头', '上一个']
  },
  chevronRight: {
    name: 'chevronRight',
    source: 'material-symbols',
    rawName: 'chevron_right',
    keywords: ['右箭头', '下一个']
  },
  swapHoriz: {
    name: 'swapHoriz',
    source: 'material-symbols',
    rawName: 'swap_horiz',
    keywords: ['滑动', '切换', '横向']
  },
  shield: {
    name: 'shield',
    source: 'material-symbols',
    rawName: 'shield',
    keywords: ['安全', '保护', '盾牌']
  },
  search: {
    name: 'search',
    source: 'material-symbols',
    rawName: 'search',
    keywords: ['搜索', '查找']
  },
  close: {
    name: 'close',
    source: 'material-symbols',
    rawName: 'close',
    keywords: ['关闭', '取消']
  },
  edit: {
    name: 'edit',
    source: 'material-symbols',
    rawName: 'edit',
    keywords: ['编辑', '修改']
  },
  delete: {
    name: 'delete',
    source: 'material-symbols',
    rawName: 'delete',
    keywords: ['删除', '移除']
  },
  add: {
    name: 'add',
    source: 'material-symbols',
    rawName: 'add',
    keywords: ['新增', '添加', '加号']
  },
  checkCircle: {
    name: 'checkCircle',
    source: 'material-symbols',
    rawName: 'check_circle',
    keywords: ['确认', '完成', '勾选']
  },
  playCircle: {
    name: 'playCircle',
    source: 'material-symbols',
    rawName: 'play_circle',
    keywords: ['播放', '演示', '视频']
  },
  gavel: {
    name: 'gavel',
    source: 'material-symbols',
    rawName: 'gavel',
    keywords: ['法律', '法槌', '法官']
  },
  formatQuote: {
    name: 'formatQuote',
    source: 'material-symbols',
    rawName: 'format_quote',
    keywords: ['引用', '引号']
  },

  // ============================================================
  // 本地 SVG（src/assets/svgs/）
  // ============================================================

  loginBoxBg: {
    name: 'loginBoxBg',
    source: 'local-svg',
    rawName: 'login-box-bg',
    keywords: ['登录框背景']
  },
  loginBg: {
    name: 'loginBg',
    source: 'local-svg',
    rawName: 'login-bg',
    keywords: ['登录背景']
  },
  error404: {
    name: 'error404',
    source: 'local-svg',
    rawName: '404',
    keywords: ['404', '未找到']
  },
  error403: {
    name: 'error403',
    source: 'local-svg',
    rawName: '403',
    keywords: ['403', '禁止访问']
  },
  error500: {
    name: 'error500',
    source: 'local-svg',
    rawName: '500',
    keywords: ['500', '服务器错误']
  },
  iconDefault: {
    name: 'iconDefault',
    source: 'local-svg',
    rawName: 'icon',
    keywords: ['默认图标']
  },
  messageSvg: {
    name: 'messageSvg',
    source: 'local-svg',
    rawName: 'message',
    keywords: ['消息']
  },
  moneySvg: {
    name: 'moneySvg',
    source: 'local-svg',
    rawName: 'money',
    keywords: ['金钱']
  },
  peoplesSvg: {
    name: 'peoplesSvg',
    source: 'local-svg',
    rawName: 'peoples',
    keywords: ['人群']
  },
  shoppingSvg: {
    name: 'shoppingSvg',
    source: 'local-svg',
    rawName: 'shopping',
    keywords: ['购物']
  }
}

/**
 * 根据 name 查找图标定义
 */
export function getIconDef(name: string): IconDefinition | null {
  return ICON_REGISTRY[name] ?? null
}

/**
 * 判断 name 是否已在注册表中
 */
export function hasIcon(name: string): boolean {
  return name in ICON_REGISTRY
}
